import { NodeType, OstNode } from '../types';
import { PortableNode, PortSide, selectBestPorts, selectDominantAxisPorts, toPortableNode } from './nodePorts';

/**
 * Canvas utility constants and helpers for drawing nodes, text wrapping, and edge paths.
 */
// Node dimension constants shared across canvas rendering and layout
export const NODE_W = 200;
export const NODE_H = 80;
export const LAYER_GAP_Y = 140;
export const NODE_GAP_X = 220;

const TEXT_X_PADDING = 10;
const TEXT_AVAILABLE_WIDTH = NODE_W - TEXT_X_PADDING * 2;
const AVERAGE_CHAR_WIDTH = 7;
const TITLE_LINE_CHARS = Math.floor(TEXT_AVAILABLE_WIDTH / AVERAGE_CHAR_WIDTH);

export const COLORS = {
    // Slightly lighter fill colors with 80% opacity and strong borders
    outcome: { bg: '#E8E2FFCC', border: '#5b4fff', text: '#a89fff', label: 'OUTCOME' },
    opportunity: { bg: '#CCFAF4CC', border: '#00b8a0', text: '#60d8c8', label: 'OPPORTUNITY' },
    solution: { bg: '#FFD7B6CC', border: '#ff6b35', text: '#ff9a70', label: 'SOLUTION' },
    story: { bg: '#DAE8FFCC', border: '#60a5fa', text: '#1e40af', label: 'STORY' },
    experiment: { bg: '#FFE6B5CC', border: '#f59e0b', text: '#92400e', label: 'EXPERIMENT' },
    // OST tree node type — a deep indigo, deliberately distinct from story's sky blue above and
    // from the unrelated Plan-board 'enabler' green below (different concept, see that entry's
    // comment). Internal literal is 'enablerNode' (not 'enabler') specifically to avoid colliding
    // with that Plan-board-only type.
    enablerNode: { bg: '#CBD5FACC', border: '#4338ca', text: '#312e81', label: 'ENABLER' },
    // Plan-board-only card type (see EnablerCardItem, types.ts) — light soft green, distinct from
    // every OST node color above since it has nothing to do with any OST tree. Keying it into this
    // same map is what lets PlanPage's connectorColorForItem (COLORS[item.type]?.border) pick a
    // sensible default connector color for an Enabler card with no extra code.
    enabler: { bg: '#DCF5E1CC', border: '#4caf6a', text: '#276a41', label: 'ENABLER' },
};

export const MOSCOW_DOT = {
    'Must have': '#ef4444', // red
    'Should have': '#f97316', // orange
    'Could have': '#eab308', // yellow (same as before)
    'Would have': '#3b82f6', // blue
};

/**
 * Fixed palette a tree's color is picked from — see getTreeColor below. Kept distinct from
 * MOSCOW_DOT's palette so a priority dot and a tree dot are never confusable at a glance.
 */
const TREE_COLOR_PALETTE = [
    '#14b8a6', // teal
    '#a855f7', // purple
    '#ec4899', // pink
    '#22c55e', // green
    '#6366f1', // indigo
    '#f59e0b', // amber
    '#0ea5e9', // sky
    '#d946ef', // fuchsia
];

/**
 * Deterministically maps a tree id (or, for legacy single-tree projects, its name) to a color
 * from TREE_COLOR_PALETTE — see PlanBoardCard.tsx's canvas-variant tree dot and its list-variant
 * tree-pill fill, both driven by this same color so a tree's identity stays visually consistent
 * across the Plan column and the Timeline canvas. Hashing the key (rather than e.g. indexing by
 * array position) means a given tree keeps the same color no matter what order the project's
 * trees happen to load in.
 */
export function getTreeColor(treeKey: string): string {
    let hash = 0;
    for (let i = 0; i < treeKey.length; i++) {
        hash = (hash * 31 + treeKey.charCodeAt(i)) | 0;
    }
    const index = Math.abs(hash) % TREE_COLOR_PALETTE.length;
    return TREE_COLOR_PALETTE[index];
}

const STATUS_BADGE_META: Record<string, { color: string; icon: string; iconXOffset?: number; iconYOffset?: number }> = {
    Draft: { color: '#ffffff', icon: '✎', iconXOffset: 0, iconYOffset: 0 },
    Idea: { color: '#ffffff', icon: '★', iconXOffset: 0, iconYOffset: 0 },
    'Ready to build': { color: '#ffff00', icon: '⚒', iconXOffset: 0, iconYOffset: 0 },
    'In progress': { color: '#ff0000', icon: '⌛', iconXOffset: 0, iconYOffset: 0 },
    'Pushed to GitHub': { color: '#ffff00', icon: '⇪', iconXOffset: 0, iconYOffset: 0 },
    Validated: { color: '#00ff00', icon: '★', iconXOffset: -0.4, iconYOffset: -0.9 },
    Done: { color: '#00ff00', icon: '✔', iconXOffset: 0, iconYOffset: -1.2 },
};

export function renderStatusBadge(status?: string) {
    if (!status) return null;

    const meta = STATUS_BADGE_META[status] || { color: '#6b7280' };
    const indicatorSize = 8;
    const offsetX = 10;
    const offsetY = 8;

    return (
        <rect
            x={NODE_W - indicatorSize - offsetX}
            y={offsetY}
            width={indicatorSize}
            height={indicatorSize}
            rx={1}
            fill={meta.color}
            stroke='#1f2937'
            strokeWidth={1}
        />
    );
}

/**
 * Calculates dynamic node height based on title wrapping and node type.
 * Returns minimum NODE_H for fixed look.
 */

export function getNodeHeight(node: OstNode) {
    const lines = splitTitle(node.title || 'Untitled');
    const lineHeight = 18;
    const topMargin = 34;
    const bottomMargin = 14;
    const dynamicHeight = topMargin + lines.length * lineHeight + bottomMargin;
    if (node.type === 'story' || node.type === 'experiment' || node.type === 'enablerNode') {
        return Math.max(NODE_H, dynamicHeight + 6);
    }
    return Math.max(NODE_H, dynamicHeight);
}

/**
 * Hierarchy depth for the node-on-node drag-to-connect gesture (Canvas.tsx) — a dragged node may
 * only be *reparented* onto a target exactly one level shallower than itself (e.g. a solution
 * dropped on an opportunity becomes that opportunity's child), which is that node's actual parent
 * type in this single-parent tree. Story/experiment nodes are intentionally omitted — they don't
 * participate in this gesture, as either source or target.
 *
 * Opportunity-onto-opportunity is the one same-type exception (see isValidConnectDragTarget):
 * opportunities can nest under other opportunities (sub-opportunities), the same shape the AI
 * "slice" feature already produces when slicing an opportunity — so despite both sides sharing a
 * level here, that pairing is still a real reparent, not a same-level link. No other same-level
 * pairing (solution↔solution) is valid at all.
 */
export const CONNECT_DRAG_LEVEL: Partial<Record<NodeType, number>> = { outcome: 0, opportunity: 1, solution: 2 };

/** True if walking up from `startId` via `resolveParentId` (start included) ever reaches
 * `searchForId` — used to check whether `searchForId` is `startId`'s own (in)direct ancestor, so
 * reparenting `searchForId` under `startId` would make it its own descendant. Bails defensively
 * if the existing data already contains a cycle, rather than looping forever. */
function chainReaches(startId: string, searchForId: string, resolveParentId: (id: string) => string | null | undefined): boolean {
    let cursor: string | null | undefined = startId;
    const seen = new Set<string>();
    while (cursor) {
        if (cursor === searchForId) return true;
        if (seen.has(cursor)) return false;
        seen.add(cursor);
        cursor = resolveParentId(cursor);
    }
    return false;
}

/**
 * True if dropping `source` onto `target` should reparent `source` under `target`, for the
 * node-on-node drag-to-connect gesture. False for: the node itself, any node without a defined
 * hierarchy level (story/experiment — excluded entirely, as either source or target), any pairing
 * that isn't either "target is one level shallower" (e.g. solution→opportunity) or "both are
 * opportunities" (sub-opportunity nesting), and direct parent/child pairs (already the case —
 * dropping a node back on its own current parent would be a no-op reparent).
 *
 * `resolveParentId` (only needed for the opportunity↔opportunity case, where a cycle is actually
 * possible) looks up a node's current parentId by id — pass e.g. `(id) => nodeMap[id]?.parentId`.
 * Without it, cycle-prevention is skipped; every caller driving real reparents should provide it.
 */
export function isValidConnectDragTarget(
    source: { id: string; type: NodeType; parentId?: string | null },
    target: { id: string; type: NodeType; parentId?: string | null },
    resolveParentId?: (id: string) => string | null | undefined,
): boolean {
    if (source.id === target.id) return false;
    const sourceLevel = CONNECT_DRAG_LEVEL[source.type];
    const targetLevel = CONNECT_DRAG_LEVEL[target.type];
    if (sourceLevel === undefined || targetLevel === undefined) return false;
    if (source.parentId === target.id || target.parentId === source.id) return false;

    const isSubOpportunityMove = source.type === 'opportunity' && target.type === 'opportunity';
    if (!isSubOpportunityMove && targetLevel !== sourceLevel - 1) return false;

    // Sub-opportunity nesting can form a cycle (drag a former ancestor back under its own
    // descendant) in a way no other pairing here can — every other valid pairing crosses a fixed
    // level, so a node can never become its own ancestor through it.
    if (isSubOpportunityMove && resolveParentId && chainReaches(target.id, source.id, resolveParentId)) {
        return false;
    }

    return true;
}

/**
 * Returns an inline style object for a node-type label (e.g. "OUTCOME").
 * Used in modal headers and panel badges where the colour must be computed
 * dynamically from the node type; falls back to a neutral grey for unknown types.
 */
export function getTypeLabelStyle(type: NodeType) {
    return {
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
    };
}

function truncate(str: string, n: number): string {
    if (!str) return 'Untitled';
    return str.length > n ? str.slice(0, n) + '…' : str;
}

/**
 * Splits title text into lines that fit node width based on character estimation.
 */
export function splitTitle(text: string): string[] {
    const normalized = (text || 'Untitled').trim();
    if (!normalized) return ['Untitled'];

    const words = normalized.split(/\s+/);
    const lines = [];
    let currentLine = '';

    words.forEach((word) => {
        const candidate = currentLine ? `${currentLine} ${word}` : word;
        if (candidate.length <= TITLE_LINE_CHARS) {
            currentLine = candidate;
        } else {
            if (currentLine) lines.push(currentLine);
            currentLine = word;
        }
    });

    if (currentLine) lines.push(currentLine);
    return lines.map((line) => truncate(line, TITLE_LINE_CHARS));
}

/**
 * Builds a rounded SVG path connecting two nodes with automatic corner smoothing.
 * Accepts explicit port positions (x1,y1) → (x2,y2) for the start and end points.
 * @returns {object} { d: pathData, color: strokeColor }
 */
export function buildEdgePath(
    from: { id: string; x?: number; y?: number; type: NodeType },
    to: { id: string; x?: number; y?: number; type: NodeType },
    nodeHeights: Record<string, number>,
): { d: string; color: string } | null {
    const fromPortable = toPortableNode(from, nodeHeights, NODE_H);
    const toPortable = toPortableNode(to, nodeHeights, NODE_H);
    return buildRectConnectorPath(fromPortable, toPortable, COLORS[from.type]?.border || '#666');
}

/**
 * Generic version of buildEdgePath's routing — the same rounded-elbow port-selection logic, but
 * for any two positioned rects (center-based x/y + width/height) and an explicit stroke color,
 * with no dependency on OST's NodeType/COLORS or the fixed NODE_W node width. buildEdgePath
 * (above) is now a thin OST-specific wrapper around this. Reused by the Plan page's Timeline
 * canvas (see GridCanvas.tsx's `connectors` prop) to route card-to-card connectors, whose cards
 * have variable, resizable dimensions unlike OST's fixed-width nodes.
 * @param gap - distance (canvas units) each port sits away from its node's own border — forwarded
 *              to selectBestPorts/getNodePorts. Defaults to 6, matching buildEdgePath's existing
 *              OST look; the Plan page's card connectors (GridCanvas.tsx) pass 0 so the line/
 *              arrowhead/origin-dot sit directly on the card's edge instead of floating off it.
 * @param rounded - forwarded to buildEdgePathFromPorts. Defaults to true, matching buildEdgePath's
 *                  existing OST look (rounded corners, falling back to sharp ones only when the
 *                  cards/nodes are too close together to fit a curve); the Plan page's card
 *                  connectors (GridCanvas.tsx) pass false for sharp corners unconditionally.
 * @param axisBias - when provided, routes via selectDominantAxisPorts (its own `heightBias`/
 *                  `widthBias`) instead of selectBestPorts's OST zone system — see
 *                  selectDominantAxisPorts's doc comment for why Plan's freely-placed cards need
 *                  a different port picker than OST's parent-child tree edges. Omitted (the
 *                  default) reproduces buildEdgePath's existing OST look exactly, including Zone
 *                  B's line suppression; the Plan page's card connectors (GridCanvas.tsx) always
 *                  pass one (typically `{ height: 0, width: 0 }` — see CONNECTOR_AXIS_BIAS).
 * @param curved - when true, ignores `rounded` entirely and routes via buildCurvedPortPath's
 *                 smooth cubic bezier instead of an elbow — the Plan page's card connectors
 *                 (GridCanvas.tsx) pass true for a curvy, hand-drafted look instead of a
 *                 rectilinear one. Default false reproduces the existing elbow routing exactly.
 * @returns null for Zone B (child entering parent from below — selectBestPorts only; never null
 *          via selectDominantAxisPorts), otherwise the path plus the resolved origin/target port
 *          points and the port side the line terminates on (`toSide`) — a renderer uses
 *          `fromPoint` for an origin marker (e.g. a filled circle) and `toSide`/`toPoint` to
 *          orient/place an arrowhead at the target end.
 */
export function buildRectConnectorPath(
    from: PortableNode,
    to: PortableNode,
    color: string,
    gap = 6,
    rounded = true,
    axisBias?: { height: number; width: number },
    curved = false,
): { d: string; color: string; fromPoint: { x: number; y: number }; toPoint: { x: number; y: number }; toSide: PortSide } | null {
    const best = axisBias
        ? selectDominantAxisPorts(from, to, gap, axisBias.height, axisBias.width)
        : selectBestPorts(from, to, gap);

    // Zone B: child is entering parent from below — suppress the line (selectBestPorts only).
    if (best === null) return null;

    let d: string;
    if (curved) {
        ({ d } = buildCurvedPortPath(best.from.x, best.from.y, best.from.side, best.to.x, best.to.y, best.to.side, color));
    } else {
        // Ports on the same axis (both top/bottom, or both left/right — selectBestPorts always
        // pairs this way, and so does selectDominantAxisPorts whenever the cards are cross-axis
        // aligned) need the 3-segment "S" elbow, since there's a real parallel gap to jog across.
        // Ports on perpendicular axes (only selectDominantAxisPorts pairs this way, for
        // cross-axis-unaligned cards) can instead take a single-bend "L" — see buildElbowPath.
        const fromAxis = best.from.side === 'left' || best.from.side === 'right' ? 'horizontal' : 'vertical';
        const toAxis = best.to.side === 'left' || best.to.side === 'right' ? 'horizontal' : 'vertical';
        ({ d } = fromAxis === toAxis
            ? buildEdgePathFromPorts(best.from.x, best.from.y, best.to.x, best.to.y, color, fromAxis === 'horizontal', rounded)
            : buildElbowPath(best.from.x, best.from.y, best.to.x, best.to.y, color, fromAxis, rounded));
    }
    return {
        d,
        color,
        fromPoint: { x: best.from.x, y: best.from.y },
        toPoint: { x: best.to.x, y: best.to.y },
        toSide: best.to.side,
    };
}

/** Outward-facing unit normal for a port side — e.g. 'left' points away from its own rect, in the
 * -x direction. The one axis-aligned direction a smooth connector's tangent should leave (or, at
 * the target end, arrive from) that port in, so the curve departs/arrives perpendicular to the
 * rect's own edge instead of at an arbitrary angle. See buildCurvedPortPath. */
function portSideNormal(side: PortSide): { x: number; y: number } {
    switch (side) {
        case 'left': return { x: -1, y: 0 };
        case 'right': return { x: 1, y: 0 };
        case 'top': return { x: 0, y: -1 };
        case 'bottom': return { x: 0, y: 1 };
    }
}

/** Longest a curved connector's bezier handle is allowed to reach, canvas units — keeps a very
 * long connector from bowing out absurdly far before curving back in. */
const CURVE_HANDLE_MAX = 120;
/** Shortest a curved connector's bezier handle is allowed to be, canvas units — keeps two very
 * close ports from producing a handle so short the curve reads as a sharp kink instead of smooth. */
const CURVE_HANDLE_MIN = 24;
/** Fraction of the straight-line port-to-port distance each bezier handle extends to, before the
 * MIN/MAX clamp above — half the distance means both handles, placed nose-to-nose, would just
 * meet in the middle for two ports that face each other head-on. */
const CURVE_HANDLE_DISTANCE_FRACTION = 0.5;

/**
 * How far (canvas units) a curved connector's bezier handle reaches out from an endpoint.
 * @param distance - straight-line distance to the endpoint it's curving toward, which sets the
 *                   handle's natural length (clamped to the MIN/MAX above).
 * @param forwardReach - how far the OTHER endpoint sits in this port's own outward direction, i.e.
 *                   the separation between the two endpoints projected onto this port's normal.
 *                   The handle is never allowed to reach past that, so the control point can't
 *                   overshoot beyond the far endpoint along the port's axis — an overshooting
 *                   handle is exactly what makes a connector bow AWAY from its target before
 *                   curving back (see buildCurvedPortPath). A negative reach means the other
 *                   endpoint is behind this port entirely, so the curve has to loop back on itself
 *                   no matter what; there we take the shortest handle allowed, which keeps that
 *                   unavoidable backward bow as small as possible.
 */
function curveHandleLength(distance: number, forwardReach: number): number {
    const naturalLength = Math.min(CURVE_HANDLE_MAX, Math.max(CURVE_HANDLE_MIN, distance * CURVE_HANDLE_DISTANCE_FRACTION));
    const maxLength = Math.max(CURVE_HANDLE_MIN, forwardReach);
    return Math.min(naturalLength, maxLength);
}

/**
 * Builds a single smooth cubic-bezier SVG path between two ports, each control point pulled
 * straight out from its own port along that port's own outward normal (see portSideNormal) — so
 * the curve always departs/arrives perpendicular to the rect edge it's anchored to, the same
 * "flowchart" look tools like draw.io/Miro use for curved connectors, rather than a raw straight
 * line between the two points. Because each control point's offset from its endpoint is purely
 * axis-aligned (the normal has a zero component on one axis), the curve's tangent at t=0 and t=1
 * lands EXACTLY on that port's own axis — which is what lets trimPathEndForArrow (GridCanvas.tsx)
 * keep pulling the visible stroke back along `toSide`'s axis for a curved path exactly as it
 * already does for a straight/elbow one, with no separate curve-tangent math needed there.
 *
 * Each handle's length is capped independently at how far the opposite endpoint actually sits in
 * that port's own outward direction (see curveHandleLength's `forwardReach`). Without that cap, a
 * shared distance-based length can push a control point past the far endpoint along the port axis,
 * which bows the connector away from its target before it curves back — e.g. a card connecting
 * rightward-and-up to a card whose entry port faces down would first dip below its own start. With
 * it, the curve always leans toward the target from the moment it leaves the port.
 */
export function buildCurvedPortPath(
    x1: number, y1: number, side1: PortSide,
    x2: number, y2: number, side2: PortSide,
    color: string,
): { d: string; color: string } {
    const distance = Math.hypot(x2 - x1, y2 - y1);
    const fromNormal = portSideNormal(side1);
    const toNormal = portSideNormal(side2);
    // Separation between the endpoints, projected onto each port's own outward normal.
    const fromReach = (x2 - x1) * fromNormal.x + (y2 - y1) * fromNormal.y;
    const toReach = (x1 - x2) * toNormal.x + (y1 - y2) * toNormal.y;
    const fromHandle = curveHandleLength(distance, fromReach);
    const toHandle = curveHandleLength(distance, toReach);
    const c1x = x1 + fromNormal.x * fromHandle;
    const c1y = y1 + fromNormal.y * fromHandle;
    const c2x = x2 + toNormal.x * toHandle;
    const c2y = y2 + toNormal.y * toHandle;
    return { d: `M ${x1} ${y1} C ${c1x} ${c1y} ${c2x} ${c2y} ${x2} ${y2}`, color };
}

type PointLike = { x?: number; y?: number };

export function buildConnectionPath(from: PointLike, to: PointLike): string {
    const x1 = from.x ?? 0;
    const y1 = from.y ?? 0;
    const x2 = to.x ?? 0;
    const y2 = to.y ?? 0;

    const dx = x2 - x1;
    const dy = y2 - y1;
    const dist = Math.sqrt(dx * dx + dy * dy) || 1;
    const midX = (x1 + x2) / 2;
    const midY = (y1 + y2) / 2;

    // Use the same gravity-style downward curve as the drag preview.
    const verticalCurve = Math.min(120, Math.max(32, dist * 0.35));
    const controlX = midX;
    const controlY = midY + verticalCurve;

    return `M ${x1} ${y1} Q ${controlX} ${controlY} ${x2} ${y2}`;
}

/**
 * Core path builder given concrete start and end coordinates.
 * @param horizontal - when true, draws a horizontal-first elbow (for left/right ports);
 *                     when false (default), draws a vertical-first elbow (for top/bottom ports).
 * @param rounded - when true (default), corners are rounded UNLESS the two parallel segments are
 *                  too close together for a smooth curve to fit, in which case it falls back to a
 *                  sharp corner automatically (OST's tree-edge look, via buildEdgePath). Pass
 *                  false to always use sharp corners, regardless of distance — the Plan page's
 *                  Timeline canvas connectors (buildRectConnectorPath) want sharp corners in every
 *                  case, not just the narrow-gap fallback.
 */
export function buildEdgePathFromPorts(
    x1: number, y1: number,
    x2: number, y2: number,
    color: string,
    horizontal = false,
    rounded = true,
): { d: string; color: string } {
    if (horizontal) {
        // Left/right port connection: go horizontally to midX, then vertically, then horizontally.
        const midX = (x1 + x2) / 2;

        if (y1 === y2) {
            // Perfectly level — straight line.
            return { d: `M ${x1} ${y1} L ${x2} ${y2}`, color };
        }

        const horizontalDistance = Math.abs(x2 - x1);
        const verticalDistance = Math.abs(y2 - y1);
        const defaultCorner = 14;
        const maxCorner = Math.min(defaultCorner, horizontalDistance / 2, verticalDistance / 2);
        const cornerRadius = Math.max(6, maxCorner);
        const parallelSegmentLength = horizontalDistance / 2 - cornerRadius;

        if (!rounded || verticalDistance < cornerRadius * 2 || parallelSegmentLength < cornerRadius / 2) {
            // Sharp corners requested outright, or the distance between two parallel horizontal
            // segments is too narrow for rounded corners, or the horizontal segment runs are
            // shorter than half the radius.
            return {
                d: [
                    `M ${x1} ${y1}`,
                    `L ${midX} ${y1}`,
                    `L ${midX} ${y2}`,
                    `L ${x2} ${y2}`,
                ].join(' '),
                color,
            };
        }

        const dirX = Math.sign(x2 - x1) || 1;
        const dirY = Math.sign(y2 - y1) || 1;

        const segments = [
            `M ${x1} ${y1}`,
            `L ${midX - dirX * cornerRadius} ${y1}`,
            `Q ${midX} ${y1} ${midX} ${y1 + dirY * cornerRadius}`,
            `L ${midX} ${y2 - dirY * cornerRadius}`,
            `Q ${midX} ${y2} ${midX + dirX * cornerRadius} ${y2}`,
            `L ${x2} ${y2}`,
        ];
        return { d: segments.join(' '), color };
    }

    // Top/bottom port connection: go vertically to midY, then horizontally, then vertically.
    const midY = (y1 + y2) / 2;

    if (x1 === x2) {
        return { d: `M ${x1} ${y1} L ${x2} ${y2}`, color };
    }

    const verticalDistance = Math.abs(y2 - y1);
    const horizontalDistance = Math.abs(x2 - x1);
    const defaultCorner = 14;
    const maxCorner = Math.min(defaultCorner, horizontalDistance / 2, verticalDistance / 2);
    const cornerRadius = Math.max(6, maxCorner);
    const parallelSegmentLength = verticalDistance / 2 - cornerRadius;

    if (!rounded || horizontalDistance < cornerRadius * 2 || parallelSegmentLength < cornerRadius / 2) {
        // Sharp corners requested outright, or the distance between two parallel vertical
        // segments is too narrow for rounded corners, or the vertical segment runs are shorter
        // than half the radius.
        const segments = [
            `M ${x1} ${y1}`,
            `L ${x1} ${midY}`,
            `L ${x2} ${midY}`,
            `L ${x2} ${y2}`,
        ];
        return { d: segments.join(' '), color };
    }

    const directionY = Math.sign(y2 - y1) || 1;
    const topY = midY - directionY * cornerRadius;
    const bottomY = midY + directionY * cornerRadius;
    const dir = Math.sign(x2 - x1) || 1;
    const startX = x1 + dir * cornerRadius;
    const endX = x2 - dir * cornerRadius;

    const segments = [
        `M ${x1} ${y1}`,
        `L ${x1} ${topY}`,
        `Q ${x1} ${midY} ${startX} ${midY}`,
        `L ${endX} ${midY}`,
        `Q ${x2} ${midY} ${x2} ${bottomY}`,
        `L ${x2} ${y2}`,
    ];

    return { d: segments.join(' '), color };
}

/**
 * Builds a 2-segment, single-bend "L" SVG path connecting a port that exits moving along one axis
 * to a port that enters moving along the perpendicular axis — e.g. a card's bottom port to
 * another card's left port (see selectDominantAxisPorts, the only port picker that returns
 * perpendicular from/to sides; buildRectConnectorPath routes here instead of
 * buildEdgePathFromPorts whenever it does). Unlike buildEdgePathFromPorts's 3-segment "S" elbow —
 * needed when both ports share an axis, since there's a parallel gap between them to jog across —
 * an L only ever needs one corner: the FROM port's own fixed coordinate (x for a vertical exit, y
 * for a horizontal one) already lines up with one end of the TO port's approach, so the corner
 * sits exactly where the two meet.
 * @param fromAxis - 'vertical' when the FROM port is top/bottom (exits moving in y), 'horizontal'
 *                  when it's left/right (exits moving in x). The TO port is always the other axis
 *                  by construction, so its fixed coordinate is inferred from this alone.
 * @param rounded - when true, rounds the single corner (radius capped by each leg's own length,
 *                  same "shrink until it fits" approach as buildEdgePathFromPorts); false always
 *                  renders a sharp corner — the Plan page's card connectors always pass false.
 */
export function buildElbowPath(
    x1: number, y1: number,
    x2: number, y2: number,
    color: string,
    fromAxis: 'vertical' | 'horizontal',
    rounded = true,
): { d: string; color: string } {
    const cornerX = fromAxis === 'vertical' ? x1 : x2;
    const cornerY = fromAxis === 'vertical' ? y2 : y1;

    const legLength1 = fromAxis === 'vertical' ? Math.abs(cornerY - y1) : Math.abs(cornerX - x1);
    const legLength2 = fromAxis === 'vertical' ? Math.abs(x2 - cornerX) : Math.abs(y2 - cornerY);
    const cornerRadius = Math.min(14, legLength1, legLength2);

    if (!rounded || cornerRadius < 1) {
        return { d: `M ${x1} ${y1} L ${cornerX} ${cornerY} L ${x2} ${y2}`, color };
    }

    if (fromAxis === 'vertical') {
        const dirY = Math.sign(cornerY - y1) || 1;
        const dirX = Math.sign(x2 - cornerX) || 1;
        const segments = [
            `M ${x1} ${y1}`,
            `L ${cornerX} ${cornerY - dirY * cornerRadius}`,
            `Q ${cornerX} ${cornerY} ${cornerX + dirX * cornerRadius} ${cornerY}`,
            `L ${x2} ${y2}`,
        ];
        return { d: segments.join(' '), color };
    }

    const dirX = Math.sign(cornerX - x1) || 1;
    const dirY = Math.sign(y2 - cornerY) || 1;
    const segments = [
        `M ${x1} ${y1}`,
        `L ${cornerX - dirX * cornerRadius} ${cornerY}`,
        `Q ${cornerX} ${cornerY} ${cornerX} ${cornerY + dirY * cornerRadius}`,
        `L ${x2} ${y2}`,
    ];
    return { d: segments.join(' '), color };
}

export function renderStatusPill(color: string, label: string) {
    const padding = 10;
    const charWidth = 7;
    const labelLength = (label || '').length;
    const pillWidth = Math.max(60, labelLength * charWidth + padding * 2);
    const pillHeight = 24;
    const radius = 2; // matches the node rect's rx so the pill's corner aligns with the node's corner

    const pathD = `M 0 ${radius}
    A ${radius} ${radius} 0 0 1 ${radius} 0
    L ${pillWidth} 0
    L ${pillWidth} ${pillHeight - radius}
    A ${radius} ${radius} 0 0 1 ${pillWidth - radius} ${pillHeight}
    L 0 ${pillHeight}
    Z`;

    return (
        <>
            <path d={pathD} fill={color} />
            <text
                x={pillWidth / 2}
                y={16}
                fill='#ffffff'
                fontSize={10}
                fontWeight={700}
                letterSpacing='0.08em'
                textAnchor='middle'
                style={{ userSelect: 'none', fontFamily: 'var(--font-monospace)' }}
            >
                {label}
            </text>
        </>
    );
}

export function renderWrappedTitle(lines: string[], startY = 54) {
    return (
        <text
            x={TEXT_X_PADDING}
            y={startY}
            fill='#3f2f24'
            fontSize={13}
            fontWeight={400}
            textAnchor='start'
            style={{ userSelect: 'none', fontFamily: 'var(--font-monospace)' }}
        >
            {lines.map((line, index) => (
                <tspan key={index} x={TEXT_X_PADDING} dy={index === 0 ? '0' : '18'}>
                    {line}
                </tspan>
            ))}
        </text>
    );
}
